// People pickers (hiring manager / coordinator / sourcer / recruiter) carry a
// `kula_list_users` search descriptor, but that agent lookup yields nothing in
// the chat form — the picker renders empty. So the FE searches the users
// itself, against the same permission the job hiring-team picker uses
// (ats-job-creation/tabs/HiringTeam/useHiringTeamFormConfig).

import {
  accountUserFullName,
  fetchAccountUsersSearch,
  type AccountUser,
} from '@/api/useAccountUsers';
import type { ClarifyFormField } from '../../../types';

// The backend's search tool for a people picker.
const USER_LOOKUP_TOOL = 'kula_list_users';

// Only users who can be put on a job are pickable.
const JOB_COLLABORATOR_PERMISSION = 'collaborate_on_jobs';

// How long a search answer stays reusable. Matches the kula Select's own async
// hook, and is short enough that a newly invited teammate shows up promptly.
const CACHE_MS = 30_000;

// `image` + `description` are what the kula Select renders as the option's
// avatar and subtitle (same shape as useAccountUsersFieldConfig).
export interface UserOption {
  value: string;
  label: string;
  description?: string;
  image?: string;
}

// A found user, before a field stamps its own value prefix on it.
interface UserRow {
  id: number;
  label: string;
  description?: string;
  image?: string;
}

export const isUserPicker = (field: ClarifyFormField): boolean =>
  field.search?.tool === USER_LOOKUP_TOOL;

const toRow = (user: AccountUser): UserRow => ({
  id: user.id,
  label: accountUserFullName(user),
  description: user.email,
  image: user.avatar_thumb_url,
});

const cache = new Map<string, { at: number; rows: UserRow[] }>();
const inFlight = new Map<string, Promise<UserRow[]>>();

// Every picker in a hiring-team form opens on the same empty query, so four
// fields would otherwise fire four identical requests. Share one request per
// query and reuse its answer briefly. Sharing is safe because the Select
// discards superseded results itself — nothing here needs to cancel a request.
const searchUsers = (query: string): Promise<UserRow[]> => {
  const cached = cache.get(query);
  if (cached && Date.now() - cached.at < CACHE_MS) {
    return Promise.resolve(cached.rows);
  }

  const pending = inFlight.get(query);
  if (pending) return pending;

  const request = fetchAccountUsersSearch(query, JOB_COLLABORATOR_PERMISSION)
    .then(({ users }) => {
      const rows = users.map(toRow);
      cache.set(query, { at: Date.now(), rows });
      return rows;
    })
    // A failed search isn't cached, so the next keystroke retries it.
    .finally(() => inFlight.delete(query));

  inFlight.set(query, request);
  return request;
};

// Values keep the backend's prefix (`user:4466`): that's what the submit ctx
// marker sends back, so a bare id wouldn't resolve server-side.
export const loadUserOptions = async (
  query: string,
  valuePrefix = ''
): Promise<UserOption[]> => {
  const rows = await searchUsers(query);
  return rows.map((row) => ({
    value: `${valuePrefix}${row.id}`,
    label: row.label,
    description: row.description,
    image: row.image,
  }));
};
